import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import serverURL from "../config";
import toast from 'react-hot-toast';
import SingleQuesModal from "../components/SingleQuesModal";
import BulkQuesModal from "../components/BulkQuesModal";
import ConfirmationModal from "../components/ConfirmationModal";


const CategoryDetails = () => {
    const { id, name } = useParams();
    const location = useLocation();
    const { description, isActive } = location.state || {};

    const [modal, setModal] = useState({ type: null, isVisible: false });
    const [questions, setQuestions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState(null);

    const openModal = (type, question = null) => {
        setCurrentQuestion(question);
        setModal({ type, isVisible: true });
    };

    const closeModal = () => {
        setModal({ type: null, isVisible: false });
        setCurrentQuestion(null);
    };

    const handleAddQuestion = async (questionData) => {
        const token = localStorage.getItem('token');
        try {
            await axios.post(`${serverURL}/questions/create`, questionData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            fetchQuestions(); // Refresh the questions list after adding a new question
            closeModal();
        } catch (error) {
            console.error('Failed to add question:', error);
        }
    };

    const fetchQuestions = useCallback(async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${serverURL}/questions/category/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setQuestions(response.data);
        } catch (error) {
            console.error('Failed to fetch questions:', error);
            toast.error('Failed to fetch questions');
        }
    }, [id]);

    const handleDeleteQuestion = (question) => {
        setQuestionToDelete(question);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteQuestion = async () => {
        if (!questionToDelete) return;

        const token = localStorage.getItem('token');
        try {
            await axios.delete(`${serverURL}/questions/${questionToDelete.id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setQuestions(questions.filter(question => question.id !== questionToDelete.id));
            toast.success('Question deleted successfully');
            setIsDeleteModalOpen(false);
            setQuestionToDelete(null);
        } catch (error) {
            console.error('Failed to delete question:', error);
            toast.error('Failed to delete question');
        }
    };

    const cancelDeleteQuestion = () => {
        setIsDeleteModalOpen(false);
        setQuestionToDelete(null);
    };

    const handleUpdateQuestion = async () => {
        const token = localStorage.getItem('token');
        try {
            if (currentQuestion.question_type === "Programming") {
                // Prepare the data for the programming question update
                const programmingData = {
                    question_text: currentQuestion.question_text,
                    points: currentQuestion.points,
                    programming_details: {
                        programming_language: currentQuestion.programming_details.programming_language,
                        input_format: currentQuestion.programming_details.input_format,
                        output_format: currentQuestion.programming_details.output_format,
                        sample_input: currentQuestion.programming_details.sample_input,
                        sample_output: currentQuestion.programming_details.sample_output,
                        time_limit: currentQuestion.programming_details.time_limit,
                        memory_limit: currentQuestion.programming_details.memory_limit,
                    },
                    test_cases: currentQuestion.test_cases,
                };

                await axios.put(`${serverURL}/questions/${currentQuestion.id}`, programmingData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
            } else {
                // Handle other question types if necessary
                await axios.put(`${serverURL}/questions/${currentQuestion.id}`, currentQuestion, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
            }
            fetchQuestions();
            closeModal();
        } catch (error) {
            console.error('Failed to update question:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentQuestion((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleOptionChange = (index, e) => {
        const { name, value } = e.target;
        const updatedOptions = [...currentQuestion.options];
        updatedOptions[index][name] = value;
        setCurrentQuestion((prev) => ({
            ...prev,
            options: updatedOptions,
        }));
    };

    const handleCorrectOptionChange = (index) => {
        const updatedOptions = currentQuestion.options.map((option, i) => ({
            ...option,
            is_correct: i === index,
        }));
        setCurrentQuestion((prev) => ({
            ...prev,
            options: updatedOptions,
        }));
    };

    const filteredQuestions = questions.filter(question =>
        question.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        question.id.toString().includes(searchTerm)
    );

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    return (
        <div className="flex flex-col min-h-screen bg-campus-bg">
            <main className="flex-grow p-4 sm:p-6">

                <div className="bg-campus-surface p-6 sm:p-8 rounded-2xl shadow-card relative w-full max-w-5xl mx-auto border border-campus-border">
                    <div className={`absolute top-4 right-4 text-xs font-bold py-1 px-3 rounded-full border ${isActive ? 'bg-green-50 text-campus-success border-green-200' : 'bg-red-50 text-campus-error border-red-200'}`}>
                        {isActive ? 'Active' : 'Inactive'}
                    </div>
                    <div className="flex justify-between items-start mb-6">
                        <h2 className="text-3xl font-heading font-bold text-campus-text-primary pr-24">{decodeURIComponent(name)}</h2>
                    </div>
                    <div className="mb-6 bg-campus-bg p-4 rounded-xl border border-campus-border">
                        <p className="text-base text-campus-text-body">
                            <strong className="text-campus-text-primary">Description:</strong>{" "}
                            {description}
                        </p>
                    </div>
                    <div className="flex flex-wrap my-6 gap-3 pt-4 border-t border-campus-border">
                        <button
                            className="bg-campus-text-primary text-white border border-transparent px-4 py-2 rounded-lg hover:bg-campus-text-body transition-colors font-medium shadow-sm"
                            onClick={() => openModal("add")}
                        >
                            Add Question
                        </button>
                        <button
                            className="bg-campus-primary text-white border border-transparent px-4 py-2 rounded-lg hover:bg-campus-primary-dark transition-colors font-medium shadow-sm"
                            onClick={() => openModal("bulk")}
                        >
                            Bulk Upload Question
                        </button>
                    </div>
                    <div className="my-6">
                        <input
                            type="text"
                            placeholder="Search questions by ID or text..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full p-3 rounded-lg border border-campus-border bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all shadow-sm"
                        />
                    </div>
                    <div className="mb-4">
                        <h3 className="text-2xl font-heading font-bold text-campus-text-primary mb-4">Questions ({filteredQuestions.length})</h3>
                        <div className="space-y-4">
                            {filteredQuestions.length > 0 ? (
                                filteredQuestions.map((question) => (
                                    <div key={question.id} className="p-5 rounded-2xl border border-campus-border hover:shadow-hover transition-all duration-200 bg-white">
                                        <div className="flex items-center justify-between mb-3 border-b border-campus-border pb-3">
                                            <h4 className="font-semibold text-campus-text-primary">ID: {question.id}</h4>
                                            <div className={(() => {
                                                if (question.difficulty_level === 'easy') return 'text-xs font-semibold px-2 py-1 rounded-md bg-green-50 text-campus-success border border-green-200';
                                                if (question.difficulty_level === 'medium') return 'text-xs font-semibold px-2 py-1 rounded-md bg-yellow-50 text-yellow-600 border border-yellow-200';
                                                return 'text-xs font-semibold px-2 py-1 rounded-md bg-red-50 text-campus-error border border-red-200';
                                            })()}>
                                                Level: {question.difficulty_level}
                                            </div>
                                        </div>
                                        <p className="text-campus-text-primary text-lg mb-4 text-wrap"><strong className="font-heading mr-2">Q:</strong> {question.question_text}</p>

                                        {question.question_type === "Programming" && question.programming_details ? (
                                            <div className="mt-4 p-4 border border-campus-border rounded-xl bg-campus-bg text-sm">
                                                <h5 className="font-semibold text-campus-text-primary mb-2">Programming Details:</h5>
                                                <p><strong>Input Format:</strong> {question.programming_details.input_format}</p>
                                                <p><strong>Output Format:</strong> {question.programming_details.output_format}</p>
                                                <p><strong>Sample Input:</strong> {question.programming_details.sample_input}</p>
                                                <p><strong>Sample Output:</strong> {question.programming_details.sample_output}</p>
                                                <p><strong>Programming Language:</strong> {question.programming_details.programming_language}</p>
                                            </div>
                                        ) : null}

                                        {question.question_type !== "Programming" && (
                                            <>
                                                <strong>Options:</strong>
                                                <ul>
                                                    {Array.isArray(question.options) && question.options.length > 0 ? (
                                                        question.options.map((option) => (
                                                            <li key={option.id}>
                                                                {option.option_text} {option.is_correct ? "✅" : null}
                                                            </li>
                                                        ))
                                                    ) : (
                                                        <li>No options available</li>
                                                    )}
                                                </ul>
                                            </>
                                        )}

                                        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-campus-border">
                                            <button
                                                onClick={() => openModal("edit", question)}
                                                className="px-4 py-2 border border-campus-border rounded-lg text-campus-text-primary hover:bg-campus-bg font-medium transition-colors"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteQuestion(question)}
                                                className="px-4 py-2 bg-campus-primary text-white rounded-lg hover:bg-campus-primary-dark font-medium transition-colors shadow-sm"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-12 bg-campus-bg rounded-xl border border-campus-border">
                                    <p className="text-lg text-campus-text-body">No questions to display</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {modal.isVisible && (

                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-1/2 max-h-[80vh] overflow-y-auto pr-4">
                        <div className="flex justify-between items-start mb-4">
                            <button onClick={closeModal} className="text-gray-500 hover:text-gray-800">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 3rem)' }}>
                            {modal.type === "add" && <SingleQuesModal onClose={closeModal} onSubmit={handleAddQuestion} categoryId={id} />}
                            {modal.type === "bulk" && <BulkQuesModal onClose={closeModal} categoryId={id} />}
                            {modal.type === "edit" && currentQuestion && (
                                <>
                                    {currentQuestion.question_type === "Programming" ? (
                                        <>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-question" className="block mb-1">Question:</label>
                                                <input
                                                    id="edit-prog-question"
                                                    type="text"
                                                    name="question_text"
                                                    value={currentQuestion.question_text}
                                                    onChange={handleInputChange}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-points" className="block mb-1">Points:</label>
                                                <input
                                                    id="edit-prog-points"
                                                    type="number"
                                                    name="points"
                                                    value={currentQuestion.points}
                                                    onChange={handleInputChange}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <h3 className="font-bold mb-2">Programming Details:</h3>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-language" className="block mb-1">Programming Language:</label>
                                                <input
                                                    id="edit-prog-language"
                                                    type="text"
                                                    name="programming_language"
                                                    value={currentQuestion.programming_details.programming_language}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-input-format" className="block mb-1">Input Format:</label>
                                                <input
                                                    id="edit-prog-input-format"
                                                    type="text"
                                                    name="input_format"
                                                    value={currentQuestion.programming_details.input_format}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-output-format" className="block mb-1">Output Format:</label>
                                                <input
                                                    id="edit-prog-output-format"
                                                    type="text"
                                                    name="output_format"
                                                    value={currentQuestion.programming_details.output_format}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-sample-input" className="block mb-1">Sample Input:</label>
                                                <input
                                                    id="edit-prog-sample-input"
                                                    type="text"
                                                    name="sample_input"
                                                    value={currentQuestion.programming_details.sample_input}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-sample-output" className="block mb-1">Sample Output:</label>
                                                <input
                                                    id="edit-prog-sample-output"
                                                    type="text"
                                                    name="sample_output"
                                                    value={currentQuestion.programming_details.sample_output}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-time-limit" className="block mb-1">Time Limit (ms):</label>
                                                <input
                                                    id="edit-prog-time-limit"
                                                    type="number"
                                                    name="time_limit"
                                                    value={currentQuestion.programming_details.time_limit}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-prog-memory-limit" className="block mb-1">Memory Limit (MB):</label>
                                                <input
                                                    id="edit-prog-memory-limit"
                                                    type="number"
                                                    name="memory_limit"
                                                    value={currentQuestion.programming_details.memory_limit}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <h3 className="font-bold mb-2">Test Cases:</h3>
                                            {Array.isArray(currentQuestion.test_cases) && currentQuestion.test_cases.map((testCase, index) => (
                                                <div key={testCase.id ?? `${testCase.input_data}-${testCase.expected_output}-${index}`} className="mb-4 mr-4">
                                                    <label htmlFor={`edit-tc-input-${index}`} className="block mb-1">Input Data:</label>
                                                    <input
                                                        id={`edit-tc-input-${index}`}
                                                        type="text"
                                                        name="input_data"
                                                        value={testCase.input_data}
                                                        onChange={(e) => {
                                                            const updatedTestCases = [...currentQuestion.test_cases];
                                                            updatedTestCases[index].input_data = e.target.value;
                                                            setCurrentQuestion((prev) => ({
                                                                ...prev,
                                                                test_cases: updatedTestCases,
                                                            }));
                                                        }}
                                                        className="border p-2 rounded w-full"
                                                    />
                                                    <label htmlFor={`edit-tc-output-${index}`} className="block mb-1">Expected Output:</label>
                                                    <input
                                                        id={`edit-tc-output-${index}`}
                                                        type="text"
                                                        name="expected_output"
                                                        value={testCase.expected_output}
                                                        onChange={(e) => {
                                                            const updatedTestCases = [...currentQuestion.test_cases];
                                                            updatedTestCases[index].expected_output = e.target.value;
                                                            setCurrentQuestion((prev) => ({
                                                                ...prev,
                                                                test_cases: updatedTestCases,
                                                            }));
                                                        }}
                                                        className="border p-2 rounded w-full"
                                                    />
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-mcq-question" className="block mb-1">Question:</label>
                                                <input
                                                    id="edit-mcq-question"
                                                    type="text"
                                                    name="question_text"
                                                    value={currentQuestion.question_text}
                                                    onChange={handleInputChange}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label htmlFor="edit-mcq-points" className="block mb-1">Points:</label>
                                                <input
                                                    id="edit-mcq-points"
                                                    type="number"
                                                    name="points"
                                                    value={currentQuestion.points}
                                                    onChange={handleInputChange}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <h3 className="font-bold mb-2">Options:</h3>
                                            {Array.isArray(currentQuestion.options) && currentQuestion.options.map((option, index) => (
                                                <div key={option.id || `opt-${index}`} className="flex items-center mb-2 mr-4">
                                                    <label htmlFor={`edit-opt-text-${index}`} className="sr-only">Option {index + 1} text</label>
                                                    <input
                                                        id={`edit-opt-text-${index}`}
                                                        type="text"
                                                        name="option_text"
                                                        value={option.option_text}
                                                        onChange={(e) => handleOptionChange(index, e)}
                                                        className="border p-2 rounded w-full"
                                                    />
                                                    <label htmlFor={`edit-opt-correct-${index}`} className="ml-2 flex items-center">
                                                        <input
                                                            id={`edit-opt-correct-${index}`}
                                                            type="radio"
                                                            checked={option.is_correct}
                                                            onChange={() => handleCorrectOptionChange(index)}
                                                        />
                                                        <span className="ml-1">Correct</span>
                                                    </label>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                    <div className="flex justify-between">
                                        <button
                                            onClick={handleUpdateQuestion}
                                            className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                                        >
                                            Update Question
                                        </button>
                                        <button
                                            onClick={closeModal}
                                            className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                title="Delete Question"
                message="Are you sure you want to delete this event?"
                onConfirm={confirmDeleteQuestion}
                onCancel={cancelDeleteQuestion}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-red-500 hover:bg-red-600"
            />
        </div>
    );
};

export default CategoryDetails;
